import React from 'react';


// Twilio legal URLs
const TWILIO_TERMS_URL = 'https://www.twilio.com/legal/tos';
const TWILIO_PRIVACY_URL = 'https://www.twilio.com/legal/privacy';

export default function SmsConsentLegal() {
  return (
    <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter']">
      {/* SMS Consent Legal Content */}
      <main className="py-20 bg-[#111010]">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-[#363232] border border-[#504A4A] rounded-lg p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-center text-[#EBFFEE]">
              SMS Consent & Terms
            </h1>
            <p className="text-center text-[#A9A3A3] mb-8">RollOps LLC</p>
            <p className="text-center text-[#A9A3A3] mb-12">
              <strong>Effective Date:</strong> December 10, 2024
            </p>

            <div className="prose prose-lg prose-invert max-w-none">
              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">SMS Messaging Service</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  By providing your phone number and consenting to receive SMS messages from RollOps, you agree to the
                  following terms and conditions regarding our SMS messaging service.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">1. Consent to Receive Messages</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  By providing your mobile phone number and checking the consent box, you expressly consent to receive
                  SMS (text) messages from RollOps LLC (&quot;RollOps,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) at the phone number
                  you provide. These messages may include:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-6 space-y-2 ml-4">
                  <li>Account verification codes</li>
                  <li>Login authentication codes</li>
                  <li>Important account notifications</li>
                  <li>Security alerts</li>
                  <li>Service updates and reminders</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">2. Message Frequency</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  Message frequency varies depending on your account activity and the services you use. You may receive
                  messages as needed for account verification, security purposes, and important service notifications.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">3. Message and Data Rates</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  <strong>Message and data rates may apply.</strong> Standard message and data rates charged by your
                  mobile carrier apply to all SMS messages sent and received. RollOps is not responsible for any charges
                  incurred as a result of receiving SMS messages. Please check with your mobile carrier for details
                  about your messaging plan.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">4. Opt-Out Instructions</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  You may opt out of receiving SMS messages at any time by replying <strong>STOP</strong> to any message
                  you receive from RollOps. After you send the STOP message, we will send you a confirmation message to
                  confirm that you have been unsubscribed. After this, you will no longer receive SMS messages from
                  RollOps.
                </p>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  To receive help or support, reply <strong>HELP</strong> to any message you receive from RollOps.
                </p>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  You may also opt out by contacting us directly at{' '}
                  <a href="mailto:support@rollops.com" className="text-[#00D9FF] hover:underline">
                    support@rollops.com
                  </a>
                  .
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">5. Supported Carriers</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  Our SMS messaging service is available on major U.S. mobile carriers, including but not limited to:
                  AT&T, T-Mobile, Verizon, Sprint, U.S. Cellular, Boost Mobile, Cricket, Metro PCS, and others.
                  Message delivery is not guaranteed and may be affected by carrier network availability, coverage, and
                  other factors outside our control.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">6. Third-Party Service Provider</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  RollOps uses Twilio, a third-party service provider, to deliver SMS messages. By consenting to receive
                  SMS messages from RollOps, you also agree to Twilio&apos;s Terms of Service and Privacy Policy:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-6 space-y-2 ml-4">
                  <li>
                    <a
                      href={TWILIO_TERMS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#00D9FF] hover:underline"
                    >
                      Twilio Terms of Service
                    </a>
                  </li>
                  <li>
                    <a
                      href={TWILIO_PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#00D9FF] hover:underline"
                    >
                      Twilio Privacy Policy
                    </a>
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">7. Privacy and Security</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  Your phone number will be used solely for the purposes described in this consent and our{' '}
                  <a href="/privacy" className="text-[#00D9FF] hover:underline">
                    Privacy Policy
                  </a>
                  . We take reasonable measures to protect your personal information, but no method of transmission over
                  the internet or electronic storage is 100% secure.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">8. Changes to Terms</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  RollOps reserves the right to modify these SMS consent terms at any time. We will notify you of any
                  material changes by posting the updated terms on this page with a new effective date. Your continued
                  use of our SMS messaging service after such changes constitutes your acceptance of the modified terms.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">9. Contact Information</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  If you have any questions about our SMS messaging service or these terms, please contact us at:
                </p>
                <div className="bg-[#1a1a1a] p-4 rounded-lg mb-4">
                  <p className="text-[#A9A3A3] mb-2">
                    <strong>RollOps LLC</strong>
                  </p>
                  <p className="text-[#A9A3A3] mb-2">
                    Email:{' '}
                    <a href="mailto:support@rollops.com" className="text-[#00D9FF] hover:underline">
                      support@rollops.com
                    </a>
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">10. Acknowledgment</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  By providing your phone number and consenting to receive SMS messages, you acknowledge that you have
                  read, understood, and agree to be bound by these SMS Consent Terms and our Privacy Policy. You
                  represent that you are the owner or authorized user of the mobile phone number you provide.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
}







